import { Component, OnInit, ChangeDetectorRef, Optional, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { Evaluator } from 'src/app/shared/models/app.models';
import { ApiService } from 'src/app/shared/services/api.service';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { TenderHelperService } from 'src/app/shared/services/tender-helper.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';


@Component({
  selector: 'app-assign-evaluators',
  templateUrl: './assign-evaluators.component.html',
  styleUrls: ['./assign-evaluators.component.scss']
})
export class AssignEvaluatorsComponent implements OnInit {
  AppConstants = AppConstants;
  assignForm: FormGroup;
  searchTerm: string = '';
  // For now, only a single evaluator can be selected in the UI
  selectedEvaluatorIds: Set<string> = new Set();
  allEvaluators: Evaluator[] = [];
  filteredEvaluators: Evaluator[] = [];
  prOptions: { value: string; label: string }[] = [];
  isPrDisabled: boolean = false;
  isEvaluationTypeDisabled: boolean = false;
  isDialogMode: boolean = false;
  private initialParams: any = {};

  evaluationTypeOptions = [
    { value: 'Technical', label: 'Technical' },
    { value: 'Commercial', label: 'Commercial' },
    { value: 'Combined', label: 'Combined' },
    { value: 'Negotiation', label: 'Negotiation' }
  ];

  constructor(
    private fb: FormBuilder,
    private _route: ActivatedRoute,
    private _router: Router,
    private apiService: ApiService,
    private _loaderService: LoaderService,
    private cdr: ChangeDetectorRef,
    private tenderHelper: TenderHelperService,
    private toasterService: ToasterService,
    @Optional() private dialogRef?: MatDialogRef<AssignEvaluatorsComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData?: any
  ) {
    this.isDialogMode = !!this.dialogRef;
    this.initialParams = this.dialogData || {};
    this.assignForm = this.fb.group({
      pr: ['', Validators.required],
      evaluationType: ['', Validators.required],
      dueDate: [''],
      notes: ['']
    });
  }

  ngOnInit(): void {
    this.isEvaluationTypeDisabled = !!this.initialParams.lockEvaluationType;
    this.loadRouteParams();
    this.loadPRs().then(() => this.afterPrsLoaded());
  }

  /**
   * Loads PRs that are attached to a Tender or Practice only (from ForEvaluations endpoint).
   */
  async loadPRs(): Promise<void> {
    try {
      this.apiService.setBaseUrl(environment.API_URL);
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.PurchaseRequests}/ForEvaluations`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));

      let prsData: any[] = [];
      const data = response?.Data ?? response?.data;
      if (data && Array.isArray(data)) {
        prsData = data;
      } else if (Array.isArray(response)) {
        prsData = response;
      }

      const options: { value: string; label: string }[] = [];
      prsData.forEach((pr: any) => {
        const prNumber = pr.PRNumber ?? pr.prNumber ?? pr.pr ?? '';
        const prName = pr.PRName ?? pr.prName ?? pr.name ?? '';
        const label = prNumber && prName ? `${prNumber} - ${prName}` : (prNumber || prName || '');
        if (prNumber && String(prNumber).trim() !== '') {
          options.push({ value: String(prNumber).trim(), label: label.trim() || String(prNumber).trim() });
        }
      });

      const preselectedPr = this.initialParams?.prNumber ?? this.initialParams?.pr;
      if (this.isDialogMode && preselectedPr && !options.some(option => option.value === String(preselectedPr).trim())) {
        options.push({ value: String(preselectedPr).trim(), label: String(preselectedPr).trim() });
      }

      this.prOptions = options;
      this.prOptions.sort((a, b) => a.value.localeCompare(b.value));
      this.cdr.detectChanges();
    } catch (error: any) {
      const errorMessage = error?.message || 'Unknown error occurred';
      console.error(`[Assign Evaluators] Exception while loading PRs:`, errorMessage, error);
      this.prOptions = [];
      this.cdr.detectChanges();
    }
  }

  /**
   * Maps API evaluator data (UsersDTO) to Evaluator interface.
   * Handles both camelCase and PascalCase from API. Requires id; name/role have fallbacks.
   */
  private mapToEvaluators(evaluatorsData: any[]): Evaluator[] {
    return evaluatorsData
      .map(evaluator => {
        const rawId = evaluator.ID !== undefined && evaluator.ID !== null
          ? evaluator.ID
          : (evaluator.id ?? evaluator.evaluatorId ?? evaluator.EvaluatorId ?? '');
        const id = String(rawId).trim();
        const name = evaluator.EvaluatorNameEN ?? evaluator.evaluatorNameEN
          ?? evaluator.EvaluatorNameAR ?? evaluator.evaluatorNameAR
          ?? evaluator.FullName ?? evaluator.fullName
          ?? evaluator.Email ?? evaluator.email
          ?? (id ? `Evaluator ${id}` : '');
        const role = evaluator.Role ?? evaluator.role
          ?? evaluator.Position ?? evaluator.position
          ?? evaluator.Department ?? evaluator.department ?? '';
        return { id, name, role };
      })
      .filter(e => e.id != null && String(e.id).trim() !== '');
  }

  private loadRouteParams(): void {
    this._route.queryParams.subscribe(routeParams => {
      const params = this.isDialogMode ? this.initialParams : routeParams;
      // When evaluationId is present, form will be filled from GetDraftEvaluationDetails API — do not use other query params.
      if (params['evaluationId']) return;

      const prNumber = params['prNumber'] ?? params['pr'];
      if (prNumber && String(prNumber).trim() !== '') {
        this.isPrDisabled = true;
        this.prOptions = [{ value: String(prNumber).trim(), label: String(prNumber).trim() }];
        this.assignForm.patchValue({ pr: String(prNumber).trim() });
      } else {
        this.isPrDisabled = false;
        this.prOptions = [];
      }
      if (params['evaluationType']) this.assignForm.patchValue({ evaluationType: params['evaluationType'] });
      if (params['dueDate']) this.assignForm.patchValue({ dueDate: params['dueDate'] });
      if (params['notes']) this.assignForm.patchValue({ notes: params['notes'] });
      const selectedIds = params['selectedIds']?.split(',').filter((id: string) => id) || [];
      if (selectedIds.length > 0) this.selectedEvaluatorIds = new Set(selectedIds);
      this.cdr.detectChanges();
    });
  }

  /** After PRs are loaded: load draft by evaluationId, or restore last draft, or just load evaluators (from GetAssignmentDraft; no GetAllEvaluators). */
  private async afterPrsLoaded(): Promise<void> {
    const params = this.isDialogMode ? this.initialParams : this._route.snapshot.queryParams;
    const evaluationId = params['evaluationId']?.trim();
    if (evaluationId) {
      await this.loadDraftByEvaluationId(evaluationId);
      return;
    }
    const hasOtherParams = (params['prNumber'] ?? params['pr'] ?? params['evaluationType']) != null;
    if (!hasOtherParams) {
      await this.restoreDraftIfNoParams();
    } else {
      await this.loadEvaluatorsFromDraftApi();
    }
  }

  /** Load only evaluators list from GetAssignmentDraft (used when form is filled from query params; no GetAllEvaluators). */
  private async loadEvaluatorsFromDraftApi(): Promise<void> {
    try {
      this.apiService.setBaseUrl(environment.API_URL);
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.EVALUATIONS}/${AppConstants.ACTIONS.GetAssignmentDraft}`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));
      const draft = response?.data ?? response;
      const evaluatorsList = draft?.evaluators ?? draft?.Evaluators;
      if (draft && typeof draft === 'object' && Array.isArray(evaluatorsList) && evaluatorsList.length > 0) {
        this.allEvaluators = this.mapToEvaluators(evaluatorsList);
        this.filteredEvaluators = [...this.allEvaluators];
        this.cdr.detectChanges();
      }
    } catch {
      // ignore
    }
  }

  /** Fetch draft details by evaluation ID from API and fill the form. PR list is already loaded by loadPRs(); we only set the selected PR and evaluators from draft. */
  private async loadDraftByEvaluationId(evaluationId: string): Promise<void> {
    try {
      this.apiService.setBaseUrl(environment.API_URL);
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.EVALUATIONS}/${AppConstants.ACTIONS.GetDraftEvaluationDetails}/${encodeURIComponent(evaluationId)}`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));
      const draft = response?.data ?? response;
      if (!draft || typeof draft !== 'object') return;

      const evaluatorsList = draft.evaluators ?? draft.Evaluators;
      if (Array.isArray(evaluatorsList) && evaluatorsList.length > 0) {
        this.allEvaluators = this.mapToEvaluators(evaluatorsList);
        this.filteredEvaluators = [...this.allEvaluators];
      }

      if (draft.prNumber) {
        this.isPrDisabled = true;
        const prVal = draft.prNumber.trim();
        const exists = this.prOptions.some(o => o.value === prVal);
        if (!exists) {
          this.prOptions = [...this.prOptions, { value: prVal, label: prVal }].sort((a, b) => a.value.localeCompare(b.value));
        }
        this.assignForm.patchValue({ pr: prVal });
      }
      if (draft.evaluationType) this.assignForm.patchValue({ evaluationType: draft.evaluationType });
      if (draft.dueDate) this.assignForm.patchValue({ dueDate: this.normalizeDateForInput(draft.dueDate) });
      if (draft.notes != null) this.assignForm.patchValue({ notes: draft.notes });
      const selectedIds = draft.evaluatorIds ?? draft.EvaluatorIds;
      if (Array.isArray(selectedIds)) {
        this.selectedEvaluatorIds = new Set(
          selectedIds.map((id: any) => String(id).trim()).filter((id: string) => id)
        );
      }
      this.cdr.detectChanges();
    } catch (err) {
      console.warn('Could not load draft by evaluation ID', evaluationId, err);
      this.toasterService.showError('Could not load draft evaluation.', 'Error');
    }
  }

  /** Restore draft from API (most recent draft) when opening with no query params. Evaluators list comes from same API; no GetAllEvaluators call. */
  private async restoreDraftIfNoParams(): Promise<void> {
    try {
      this.apiService.setBaseUrl(environment.API_URL);
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.EVALUATIONS}/${AppConstants.ACTIONS.GetAssignmentDraft}`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));
      const draft = response?.data ?? response;
      if (!draft || typeof draft !== 'object') return;
      const evaluatorsList = draft.evaluators ?? draft.Evaluators;
      if (Array.isArray(evaluatorsList) && evaluatorsList.length > 0) {
        this.allEvaluators = this.mapToEvaluators(evaluatorsList);
        this.filteredEvaluators = [...this.allEvaluators];
      }
      if (draft.prNumber) this.assignForm.patchValue({ pr: draft.prNumber });
      if (draft.evaluationType) this.assignForm.patchValue({ evaluationType: draft.evaluationType });
      if (draft.dueDate) this.assignForm.patchValue({ dueDate: this.normalizeDateForInput(draft.dueDate) });
      if (draft.notes != null) this.assignForm.patchValue({ notes: draft.notes });
      const selectedIds = draft.evaluatorIds ?? draft.EvaluatorIds;
      if (Array.isArray(selectedIds)) {
        this.selectedEvaluatorIds = new Set(
          selectedIds.map((id: any) => String(id).trim()).filter((id: string) => id)
        );
      }
      this.cdr.detectChanges();
    } catch {
      // ignore
    }
  }

  /** Normalize API date (e.g. "2026-02-13T00:00:00") to YYYY-MM-DD for date input. */
  private normalizeDateForInput(value: any): string | null {
    if (value == null) return null;
    const s = typeof value === 'string' ? value : String(value);
    const match = s.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : s;
  }

  filterEvaluators(): void {
    const search = (this.searchTerm ?? '').toLowerCase().trim();
    this.filteredEvaluators = search
      ? this.allEvaluators.filter(e =>
          (e.name && e.name.toLowerCase().includes(search)) ||
          (e.role && e.role.toLowerCase().includes(search))
        )
      : [...this.allEvaluators];
  }

  onSearchChange(): void {
    this.filterEvaluators();
  }

  toggleEvaluatorSelection(evaluatorId: string): void {
    // Multi-select: toggle this evaluator in/out of selection
    if (this.selectedEvaluatorIds.has(evaluatorId)) {
      this.selectedEvaluatorIds.delete(evaluatorId);
    } else {
      this.selectedEvaluatorIds.add(evaluatorId);
    }
  }

  isEvaluatorSelected(evaluatorId: string): boolean {
    return this.selectedEvaluatorIds.has(evaluatorId);
  }

  getSelectedEvaluators(): Evaluator[] {
    return this.allEvaluators.filter(evaluator => this.selectedEvaluatorIds.has(evaluator.id));
  }

  onCancel(): void {
    if (this.isDialogMode) {
      this.dialogRef?.close({ action: 'cancel' });
      return;
    }
    this._router.navigate([`/${AppConstants.ROUTES.EVALUATIONS}`]);
  }

  /**
   * Prepares request data for API
   * Sends prNumber (PRs dropdown); backend resolves to Tender when needed
   */
  private prepareRequestData(): any {
    const formValue = this.assignForm.getRawValue();

    const evaluatorIds = Array.from(this.selectedEvaluatorIds)
      .map(id => String(id))
      .filter(id => id && id.trim() !== '');

    const dueDate = this.tenderHelper.formatDateForAPI(formValue.dueDate);
    const prNumber = this.tenderHelper.getTrimmedValue(formValue.pr);
    const evaluationType = this.tenderHelper.getTrimmedValue(formValue.evaluationType);
    const notes = this.tenderHelper.getTrimmedValue(formValue.notes) || null;

    return {
      prNumber: prNumber || '',
      evaluationType: evaluationType || '',
      evaluatorIds: evaluatorIds,
      dueDate: dueDate,
      notes: notes
    };
  }

  /**
   * Handles form submission and API call
   */
  private async handleFormSubmission(action: 'draft' | 'publish' | 'assign'): Promise<void> {
    if (action === 'draft') {
      const pr = this.assignForm.get('pr')?.value?.trim();
      if (!pr) {
        this.toasterService.showError('Please select a PR before saving the draft.', 'Validation');
        this.assignForm.get('pr')?.markAsTouched();
        return;
      }
      this._loaderService.showLoader();
      try {
        await this.saveDraftToApi();
        this.toasterService.showSuccess('Draft saved. You can continue later.', 'Draft saved');
      } catch (err: any) {
        this.toasterService.showError(err?.error?.error ?? err?.message ?? 'Failed to save draft', 'Error');
      } finally {
        this._loaderService.hideLoader();
      }
      return;
    }

    if (!this.assignForm.valid) {
      this.assignForm.markAllAsTouched();
      return;
    }

    if ((action === 'publish' || action === 'assign') && this.selectedEvaluatorIds.size === 0) {
      this.toasterService.showError('Please select at least one evaluator', 'Validation Error');
      this.assignForm.markAllAsTouched();
      return;
    }

    this._loaderService.showLoader();
    
    try {
      this.apiService.setBaseUrl(environment.API_URL);
      const requestData = this.prepareRequestData();
      
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.EVALUATIONS}/${AppConstants.ACTIONS.AssignEvaluators}`;
      const response: any = await lastValueFrom(this.apiService.postByUrl(url, requestData));

      // Handle response - check for success
      const isSuccess = this.tenderHelper.isSuccessResponse(response);
      
      if (isSuccess || response) {
        const actionMessages = {
          'publish': 'Tender published successfully',
          'assign': 'Evaluators assigned successfully'
        };
        
        this.toasterService.showSuccess(actionMessages[action], 'Success');
        try {
          const clearUrl = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.EVALUATIONS}/${AppConstants.ACTIONS.ClearAssignmentDraft}`;
          await lastValueFrom(this.apiService.postByUrl(clearUrl, {}));
        } catch { }

        if (this.isDialogMode) {
          this.dialogRef?.close({ action: 'assigned' });
        } else {
          // Navigate to evaluations page on success
          setTimeout(() => {
            this._router.navigate([`/${AppConstants.ROUTES.EVALUATIONS}`]);
          }, 1500);
        }
      } else {
        const errorMessage = this.tenderHelper.getResponseErrorMessage(response, 'Failed to assign evaluators');
        this.toasterService.showError(errorMessage, 'Error');
      }
      
    } catch (error: any) {
      const errorMessage = this.tenderHelper.getErrorMessage(error) || 'Failed to assign evaluators';
      console.error(`[Assign Evaluators] Error:`, errorMessage, error);
      this.toasterService.showError(errorMessage, 'Error');
    } finally {
      this._loaderService.hideLoader();
    }
  }

  /**
   * Saves current form state and selected evaluator IDs to DB as draft (status Draft). No assign.
   * Backend expects EvaluatorIds as List<string>; ensure we always send strings.
   */
  async saveDraftToApi(): Promise<void> {
    const formValue = this.assignForm.getRawValue();
    const evaluatorIds = Array.from(this.selectedEvaluatorIds)
      .map(id => String(id))
      .filter(id => id != null && id !== '');
    const draft = {
      prNumber: formValue.pr ?? '',
      evaluationType: formValue.evaluationType ?? '',
      dueDate: formValue.dueDate || null,
      notes: formValue.notes ?? '',
      evaluatorIds
    };
    try {
      this.apiService.setBaseUrl(environment.API_URL);
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.EVALUATIONS}/${AppConstants.ACTIONS.SaveAssignmentDraft}`;
      await lastValueFrom(this.apiService.postByUrl(url, draft));
    } catch (e) {
      console.warn('Could not save draft', e);
      throw e;
    }
  }

  onSaveDraft(): void {
    this.handleFormSubmission('draft');
  }

  onAssign(): void {
    this.handleFormSubmission('assign');
  }

  onPublish(): void {
    this.handleFormSubmission('publish');
  }

  goBack(): void {
    if (this.isDialogMode) {
      this.dialogRef?.close({ action: 'cancel' });
      return;
    }
    this._router.navigate([`/${AppConstants.ROUTES.EVALUATIONS}`]);
  }
}

